import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BillingIntervalToggle } from '@/features/billing/components/BillingIntervalToggle';
import { useBilling } from '@/features/billing/hooks/useBilling';
import {
  FAMILY_PRICING,
  PLUS_PRICING,
} from '@/features/billing/lib/planPricing';
import type { PackageId, SubscriptionType } from '@/features/billing/types';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Flex, SimpleGrid, Text } from '@/ui';
import { PlanOptionCard } from './PlanOptionCard';

// The Free user's upgrade paths, inline in the settings panel instead of behind
// the compare-plans modal. Organizations is deliberately absent — buying a
// workspace lives on the "Create organization" panel, since it provisions a
// separate account rather than upgrading this one.
export const InlineUpgradeChoices = () => {
  const { t } = useTranslation('settingsModal', {
    keyPrefix: 'subscription.upgrade',
  });
  const [interval, setInterval] = useState<SubscriptionType>('yearly');
  const { redirectToCheckout } = useBilling();
  const { trackEvent } = useAnalytics();

  const handleSelect = async (packageId: PackageId) => {
    trackEvent('settings:click upgrade', {
      target: 'button',
      launch: 'settings:subscription',
    });
    await redirectToCheckout(
      interval,
      { launch: 'settings:subscription' },
      packageId
    );
  };

  const plus = PLUS_PRICING[interval];
  const family = FAMILY_PRICING[interval];

  return (
    <Flex direction="column" gap="6" mt="10">
      <Flex align="center" gap="4" justify="space-between">
        <Text fontSize="lg" fontWeight="medium">
          {t('heading')}
        </Text>
        <BillingIntervalToggle
          annualLabel={t('annual')}
          ariaLabel={t('billing_interval')}
          monthlyLabel={t('monthly')}
          value={interval}
          onChange={setInterval}
        />
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
        <PlanOptionCard
          badgeLabel={t('recommended')}
          ctaLabel={t('plus.cta')}
          isRecommended
          name={t('plus.name')}
          period={plus.period}
          price={plus.price}
          seatsLabel={t('plus.seats')}
          features={[
            t('plus.feature_magic'),
            t('plus.feature_co_editors'),
            t('plus.feature_sources'),
          ]}
          onSelect={() => handleSelect(plus.packageId)}
        />
        <PlanOptionCard
          ctaLabel={t('family.cta')}
          name={t('family.name')}
          period={family.period}
          price={family.price}
          seatsLabel={t('family.seats')}
          features={[
            t('family.feature_everything'),
            t('family.feature_reminders'),
            t('family.feature_calendars'),
          ]}
          onSelect={() => handleSelect(family.packageId)}
        />
      </SimpleGrid>
    </Flex>
  );
};
